export interface ExportColumn {
  key: string;
  label: string;
}

export type ExportRow = Record<string, unknown>;

function safeFilePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, "_").slice(0, 80) || "Salesforce结果";
}

export function resultFileName(title: string, extension: string, now = new Date()): string {
  const date = now.toISOString().slice(0, 10);
  return `${safeFilePart(title)}_${date}.${extension}`;
}

function cellText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

function csvCell(value: unknown): string {
  const raw = cellText(value);
  const safe = /^[=+\-@]/.test(raw) ? `'${raw}` : raw;
  return `"${safe.replace(/"/g, '""')}"`;
}

export function createCsvBlob(columns: ExportColumn[], rows: ExportRow[]): Blob {
  const lines = [
    columns.map(({ label }) => csvCell(label)).join(","),
    ...rows.map((row) => columns.map(({ key }) => csvCell(row[key])).join(","))
  ];
  return new Blob(["\ufeff", lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
}

function xmlText(value: unknown): string {
  return cellText(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function excelCell(value: unknown, styleId?: string): string {
  const style = styleId ? ` ss:StyleID="${styleId}"` : "";
  if (typeof value === "number" && Number.isFinite(value)) {
    return `<Cell${style}><Data ss:Type="Number">${value}</Data></Cell>`;
  }
  if (typeof value === "boolean") {
    return `<Cell${style}><Data ss:Type="Boolean">${value ? 1 : 0}</Data></Cell>`;
  }
  return `<Cell${style}><Data ss:Type="String">${xmlText(value)}</Data></Cell>`;
}

/**
 * Lightweight Excel 2003 XML keeps the embedded assistant bundle small while
 * still producing a native Excel workbook with typed cells and a frozen header.
 */
export function createExcelBlob(title: string, columns: ExportColumn[], rows: ExportRow[]): Blob {
  const worksheetName = xmlText(title.slice(0, 31) || "查询结果");
  const columnXml = columns.map(({ label }) => {
    const width = Math.min(220, Math.max(72, label.length * 14 + 28));
    return `<Column ss:AutoFitWidth="1" ss:Width="${width}"/>`;
  }).join("");
  const rowsXml = [
    `<Row ss:StyleID="Header">${columns.map(({ label }) => excelCell(label, "Header")).join("")}</Row>`,
    ...rows.map((row) => `<Row>${columns.map(({ key }) => excelCell(row[key])).join("")}</Row>`)
  ].join("");
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <DocumentProperties xmlns="urn:schemas-microsoft-com:office:office"><Title>${worksheetName}</Title></DocumentProperties>
 <Styles>
  <Style ss:ID="Default" ss:Name="Normal"><Alignment ss:Vertical="Top"/><Font ss:FontName="Microsoft YaHei" ss:Size="10"/></Style>
  <Style ss:ID="Header"><Font ss:FontName="Microsoft YaHei" ss:Size="10" ss:Bold="1" ss:Color="#17324D"/><Interior ss:Color="#EAF3FB" ss:Pattern="Solid"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#BFD2E3"/></Borders></Style>
 </Styles>
 <Worksheet ss:Name="${worksheetName}">
  <Table>${columnXml}${rowsXml}</Table>
  <WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel"><FreezePanes/><FrozenNoSplit/><SplitHorizontal>1</SplitHorizontal><TopRowBottomPane>1</TopRowBottomPane><ActivePane>2</ActivePane></WorksheetOptions>
 </Worksheet>
</Workbook>`;
  return new Blob([xml], { type: "application/vnd.ms-excel;charset=utf-8" });
}

export function createSvgBlob(svg: SVGSVGElement): Blob {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  return new Blob([new XMLSerializer().serializeToString(clone)], { type: "image/svg+xml;charset=utf-8" });
}

export async function createPngBlob(svg: SVGSVGElement, scale = 2): Promise<Blob> {
  const svgBlob = createSvgBlob(svg);
  const url = URL.createObjectURL(svgBlob);
  try {
    const image = new Image();
    image.decoding = "async";
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("图表图片生成失败"));
      image.src = url;
    });
    const viewBox = svg.viewBox.baseVal;
    const width = Math.max(1, viewBox.width || svg.clientWidth || 720);
    const height = Math.max(1, viewBox.height || svg.clientHeight || 360);
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(width * scale);
    canvas.height = Math.ceil(height * scale);
    const context = canvas.getContext("2d");
    if (!context) throw new Error("当前浏览器不支持图片导出");
    context.scale(scale, scale);
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    return await new Promise<Blob>((resolve, reject) => canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error("图表图片生成失败")),
      "image/png"
    ));
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = "noopener";
  anchor.style.display = "none";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
